import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { defer, forkJoin, Observable, Subject, timer } from 'rxjs';
import { finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ResultWithTotal } from 'src/app/api/models/OK/Egis/Service/Business/result-with-total';
import { RecentServiceafregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/recent-serviceafregning-dto';
import { OpkraevningScopeEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opkraevning-scope-enum';
import { ServiceafregningBasisEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceafregning-basis-enum';
import { StartAfregningRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/start-afregning-request';
import { PriceService, SystemStatusService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { StartAfregningDialogComponent } from '../start-afregning-dialog/start-afregning-dialog.component';
import { SignalrService } from 'src/app/services/signalr.service';
import { ServiceaftalePricecacheUpdateDto } from 'src/app/api/models/OK/Egis/Service/Models/Response/serviceaftale-pricecache-update-dto';

@Component({
    selector: 'app-system-status-opkraevninger',
    templateUrl: './system-status-opkraevninger.component.html',
    styleUrls: ['./system-status-opkraevninger.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemStatusOpkraevningerComponent implements OnInit, OnDestroy {
  TIMER = 60000;

  ServiceafregningBasisEnum = ServiceafregningBasisEnum;
  OpkraevningScopeEnum = OpkraevningScopeEnum;

  columnsRecent: string[] = ['timestamp', 'bilagId', 'oKAftaleNr', 'ordreNo', 'amount'];
  columnsEftersyn: string[] = ['timestamp', 'oKAftaleNr', 'ordreNo'];
  columnsAftaleQueue: string[] = ['timestamp', 'oKAftaleNr', 'actions'];
  columnsAfregningQueue: string[] = ['timestamp', 'oKAftaleNr', 'serviceordreNo', 'actions'];

  isAdmin: boolean = false;

  isLoading: boolean = false;

  isConnected: boolean;

  unsubscribe$ = new Subject<void>();

  nextScheduledOpkraevning: Date;

  recentServiceafregningerLoading$ = new Subject<boolean>();
  afregningerWithoutBilagLoading$ = new Subject<boolean>();
  waitingPartnerServiceordreafregningerLoading$ = new Subject<boolean>();
  waitingCustomerServiceordreafregningLoading$ = new Subject<boolean>();
  waitingPartnerkrediteringLoading$ = new Subject<boolean>();
  waitingCustomerOpkraevningLoading$ = new Subject<boolean>();

  recentServiceafregninger: RecentServiceafregningDto[];

  waitingPartnerServiceordreafregninger: [] | null;
  waitingPartnerServiceordreafregningerTotal: number | null;

  afregningerWithoutBilag: [] | null;
  afregningerWithoutBilagTotal: number | null;

  waitingCustomerServiceordreafregning: [] | null;
  waitingCustomerServiceordreafregningTotal: number | null;

  waitingPartnerkreditering: [] | null;
  waitingPartnerkrediteringTotal: number | null;

  waitingCustomerOpkraevning: [] | null;
  waitingCustomerOpkraevningTotal: number | null;

  getNextScheduledOpkraevning$: Observable<string>;
  getRecentAfregninger$: Observable<RecentServiceafregningDto[]>;
  getWaitingPartnerServiceordreafregning$: Observable<ResultWithTotal>;
  geAfregningerWithoutBilag$: Observable<ResultWithTotal>;
  getWaitingCustomerServiceordreafregning$: Observable<ResultWithTotal>;
  getWaitingPartnerkreditering$: Observable<ResultWithTotal>;
  getWaitingCustomerOpkraevning$: Observable<ResultWithTotal>;

  constructor(
    private systemStatusService: SystemStatusService,
    private priceService: PriceService,
    private router: Router,
    private dialog: MatDialog,
    private auth: AuthService,
    private loader: LoaderService,
    private notification: NotificationService,
    private signalR: SignalrService
  ) {
    this.isAdmin = this.auth.isAdministrator;
  }

  ngOnInit(): void {
    let recentServiceafregningerLoaderRef = this.loader.createLoader();
    recentServiceafregningerLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.recentServiceafregningerLoading$.next(shouldShow);
    });

    let waitingPartnerServiceordreafregningerLoaderRef = this.loader.createLoader();
    waitingPartnerServiceordreafregningerLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.waitingPartnerServiceordreafregningerLoading$.next(shouldShow);
    });

    let afregningerWithoutBilagLoaderRef = this.loader.createLoader();
    afregningerWithoutBilagLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.afregningerWithoutBilagLoading$.next(shouldShow);
    });

    let waitingCustomerServiceordreafregningLoaderRef = this.loader.createLoader();
    waitingCustomerServiceordreafregningLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.waitingCustomerServiceordreafregningLoading$.next(shouldShow);
    });

    let waitingPartnerkrediteringLoaderRef = this.loader.createLoader();
    waitingPartnerkrediteringLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.waitingPartnerkrediteringLoading$.next(shouldShow);
    });

    let waitingCustomerOpkraevningLoaderRef = this.loader.createLoader();
    waitingCustomerOpkraevningLoaderRef.showStateChanged$.subscribe((shouldShow: boolean) => {
      this.waitingCustomerOpkraevningLoading$.next(shouldShow);
    });

    this.getNextScheduledOpkraevning$ = defer(() => {
      return this.systemStatusService.getNextScheduledOpkraevning({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: string) => {
          this.nextScheduledOpkraevning = new Date(result);
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {})
      );
    });

    this.getRecentAfregninger$ = defer(() => {
      recentServiceafregningerLoaderRef.start();
      return this.systemStatusService.getRecentAfregninger({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: RecentServiceafregningDto[]) => {
          this.recentServiceafregninger = result;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          recentServiceafregningerLoaderRef.stop();
        })
      );
    });

    this.getWaitingPartnerServiceordreafregning$ = defer(() => {
      waitingPartnerServiceordreafregningerLoaderRef.start();
      return this.systemStatusService.getWaitingPartnerServiceordreafregning({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.waitingPartnerServiceordreafregninger = result.result;
          this.waitingPartnerServiceordreafregningerTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          waitingPartnerServiceordreafregningerLoaderRef.stop();
        })
      );
    });

    this.geAfregningerWithoutBilag$ = defer(() => {
      afregningerWithoutBilagLoaderRef.start();
      return this.systemStatusService.geAfregningerWithoutBilag({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.afregningerWithoutBilag = result.result;
          this.afregningerWithoutBilagTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          afregningerWithoutBilagLoaderRef.stop();
        })
      );
    });

    this.getWaitingCustomerServiceordreafregning$ = defer(() => {
      waitingCustomerServiceordreafregningLoaderRef.start();
      return this.systemStatusService.getWaitingCustomerServiceordreafregning({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.waitingCustomerServiceordreafregning = result.result;
          this.waitingCustomerServiceordreafregningTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          waitingCustomerServiceordreafregningLoaderRef.stop();
        })
      );
    });

    this.getWaitingPartnerkreditering$ = defer(() => {
      waitingPartnerkrediteringLoaderRef.start();
      return this.systemStatusService.getWaitingPartnerkreditering({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.waitingPartnerkreditering = result.result;
          this.waitingPartnerkrediteringTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          waitingPartnerkrediteringLoaderRef.stop();
        })
      );
    });

    this.getWaitingCustomerOpkraevning$ = defer(() => {
      waitingCustomerOpkraevningLoaderRef.start();
      return this.systemStatusService.getWaitingCustomerOpkraevning({ caller: EGIS_SERVICE_CALLER }).pipe(
        tap((result: ResultWithTotal) => {
          this.waitingCustomerOpkraevning = result.result;
          this.waitingCustomerOpkraevningTotal = result.count;
        }),
        takeUntil(this.unsubscribe$),
        finalize(() => {
          waitingCustomerOpkraevningLoaderRef.stop();
        })
      );
    });

    timer(0, this.TIMER)
      .pipe(
        takeUntil(this.unsubscribe$),
        flatMap(() => {
          return forkJoin([
            this.getNextScheduledOpkraevning$,

            this.getRecentAfregninger$,
            this.getWaitingPartnerServiceordreafregning$,
            this.geAfregningerWithoutBilag$,
            this.getWaitingCustomerServiceordreafregning$,
            this.getWaitingPartnerkreditering$,
            this.getWaitingCustomerOpkraevning$,
          ]);
        })
      )
      .subscribe(
        () => {},
        (error) => {
          this.handleError(error);
        }
      );

    this.signalR.isConnected$.subscribe(
      (isConnected: boolean) => {
        console.log('isConnected', isConnected);
        this.isConnected = isConnected;
      },
      (e) => {
        console.log(e);
      }
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onRowClick(row: any) {
    this.router.navigate(['/aftale', row.okKundeAftaleNr]);
  }

  onStartAfregningerClick() {
    const dialogRef = this.dialog.open(StartAfregningDialogComponent);
    dialogRef.afterClosed().subscribe((result: StartAfregningRequest) => {
      if (result) {
        this.onStartAfregningClick(result.serviceafregningBasis, result.opkraevningScope);
      }
    });
  }

  onRecalculateAllAgreementsClick() {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Er du sikker?',
        body: 'Dette vil genberegne prisen på alle aftaler til rapportering og tager lang tid. Systemet kan blive ustabilt mens beregningen står på',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.isLoading = true;
        this.signalR.serviceaftalePriceCacheUpdated$.pipe(takeUntil(this.unsubscribe$)).subscribe(
          (result: ServiceaftalePricecacheUpdateDto) => {
            console.log('updated in ' + result.elapsedSeconds + 'seconds');
            this.isLoading = false;
            this.notification.success('Alle priser er opdateret.');
          },
          (e) => {}
        );

        this.signalR.serviceaftalePriceCacheFailed$.pipe(takeUntil(this.unsubscribe$)).subscribe(
          (result: string) => {
            this.isLoading = false;
            this.notification.error(result);
          },
          (e) => {}
        );

        this.priceService
          .recalculatePriceForAllAgreements({
            caller: EGIS_SERVICE_CALLER
          })
          .pipe(finalize(() => {}))
          .subscribe(
            () => {},
            (e) => {
              this.handleError(e);
            }
          );
      }
    });
  }

  onStartAfregningClick(serviceafregningBasis: ServiceafregningBasisEnum, opkraevningScope: OpkraevningScopeEnum) {
    const request: StartAfregningRequest = {
      serviceafregningBasis: serviceafregningBasis,
      opkraevningScope: opkraevningScope,
    };
    this.loader.show();
    this.systemStatusService
      .startAfregning({ body: request, caller: EGIS_SERVICE_CALLER })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        () => {
          this.notification.success('Afregning startet.');
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  handleError(error: any) {
    console.log('error', error);
    this.notification.error('Der er sket en fejl.');
  }
}
